import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

import { NODES_PER_PAGE } from 'src/const/app';
import type { AppliedJob, SavedJob } from 'src/types/graphql';

const usePagination = (nodes: SavedJob[] | AppliedJob[] = []) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const currentPage = parseInt(searchParams.get('page') as string, 10) || 1;
  const startIndex = (currentPage - 1) * NODES_PER_PAGE;
  const endIndex = startIndex + NODES_PER_PAGE;
  const currentNodes = nodes.slice(startIndex, endIndex);
  const totalPages = Math.ceil(nodes.length / NODES_PER_PAGE) || 1;
  const setCurrentPage = (page = 1) => {
    if (page > 1) {
      searchParams.set('page', page.toString());
      setSearchParams(searchParams);
    } else if (searchParams.has('page')) {
      searchParams.delete('page');
      setSearchParams(searchParams);
    }
  };

  // If parsedPage is higher than totalPages, go to last page with nodes
  // Must be done this way as nodes === 0 on initial render
  useEffect(() => {
    if (nodes.length > 0 && currentNodes.length === 0) {
      setCurrentPage(totalPages);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentNodes]);

  useEffect(() => {
    if (currentPage === 1) setCurrentPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  return {
    currentNodes,
    currentPage: Math.min(currentPage, totalPages),
    setCurrentPage,
    totalPages,
  };
};

export { usePagination };
